import "twin.macro";

import { useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { Add, Edit, Remove } from "@mui/icons-material";
import { Button, IconButton, Menu, Tooltip } from "@mui/material";

import _ from "lodash";

import { ControlledAutocompletePopper } from "@utils/forms";

export type DistributorUserEntitySelectorProps = {
  distFieldName: string;
  userFieldName: string;
  orgOptions: any[];
  limitTags?: number;
  label: string;
  disabled?: boolean;
};

const DistributorUserEntitySelector = ({
  distFieldName,
  userFieldName,
  orgOptions,
  limitTags = 1,
  label,
  disabled,
}: DistributorUserEntitySelectorProps) => {
  const { control, getValues, setValue } = useFormContext();
  const distEntIds = useWatch({ name: distFieldName }) ?? [];
  const userEntIds = useWatch({ name: userFieldName }) ?? [];

  // Get the subset of org options that are in the distributor's entityIds
  const options = _(orgOptions)
    .filter((t) => distEntIds.includes(t.id))
    .sortBy("name")
    .value();
  const selectedOrgEntities = options.filter((t) => userEntIds.includes(t.id));

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const noOptions = selectedOrgEntities.length === 0;
  const allSelected =
    selectedOrgEntities.length > 0 &&
    selectedOrgEntities.length === options.length;
  const partialSelected = !noOptions && !allSelected;

  function handleToggleAll() {
    if (allSelected) {
      setValue(
        userFieldName,
        getValues(userFieldName).filter(
          (v: string) => !options.some((o) => o.id === v)
        ),
        { shouldDirty: true }
      );
    } else {
      setValue(
        userFieldName,
        _.uniq([...getValues(userFieldName), ...options.map((o) => o.id)]),
        { shouldDirty: true }
      );
    }
  }

  return (
    <div>
      <Button
        variant="text"
        endIcon={noOptions ? <Add /> : <Edit />}
        onClick={handleClick}
        color={noOptions ? "error" : "primary"}
        disabled={disabled}
        tw="leading-4 -ml-2"
      >
        {noOptions && `No ${label}`}
        {allSelected && `All ${label}`}
        {partialSelected && (
          <span>
            {selectedOrgEntities
              .slice(0, limitTags)
              .map((e) => e.name)
              .join(", ")}
            {selectedOrgEntities.length > limitTags &&
              ` +${selectedOrgEntities.length - limitTags}`}
          </span>
        )}
      </Button>
      {anchorEl && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
        >
          <ControlledAutocompletePopper
            control={control}
            name={userFieldName}
            options={options}
            multiple
            getSubmitValue={(value) =>
              value.map((v) => (typeof v === "string" ? v : v.id))
            }
            inputPlaceholder={`Search for ${label}`}
            disableCloseOnSelect
            inputEndAdornment={
              <Tooltip
                title={allSelected ? "Remove all" : "Add all"}
                placement="top"
              >
                <IconButton onClick={handleToggleAll} size="small">
                  {allSelected ? <Remove /> : <Add />}
                </IconButton>
              </Tooltip>
            }
          />
        </Menu>
      )}
    </div>
  );
};

export default DistributorUserEntitySelector;
